import { ArrowBack, ChevronRight, Restore } from "@mui/icons-material";
import { Alert, Box, Button, Card, Chip, CircularProgress, Stack, Typography } from "@mui/joy";
import { useMemo, useState } from "react";

import {
  useRouteLabelConfigQuery,
  useUpdateRouteLabelGroupMutation,
  useUpdateRouteLabelMutation,
} from "@/hooks/routeLabels";
import { useCurrentVenueQuery } from "@/hooks/venues";

import { ButtonLink } from "../Links";
import { splitRouteLabelConfig } from "./routeLabelsConfig";
import { RouteLabelsToast } from "./routeLabelsToast";

export default function ArchivedRouteLabels() {
  const { data: venue } = useCurrentVenueQuery();
  const { data: config, isPending, isError } = useRouteLabelConfigQuery();
  const updateGroup = useUpdateRouteLabelGroupMutation();
  const updateLabel = useUpdateRouteLabelMutation();

  const { archivedGroups, archivedRootLabels, archivedOptionSections, archivedCount } = useMemo(
    () => splitRouteLabelConfig(config),
    [config],
  );
  const hasArchivedLabels = archivedGroups.length > 0 || archivedRootLabels.length > 0;

  const [expandedUuids, setExpandedUuids] = useState<Set<string>>(new Set());
  const toggle = (uuid: string) =>
    setExpandedUuids((current) => {
      const next = new Set(current);
      if (!next.delete(uuid)) {
        next.add(uuid);
      }
      return next;
    });

  if (venue && venue.userRole !== "ADMIN") {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Only gym admins can manage route labels.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5, height: "100dvh", overflow: "auto" }}>
      <Box sx={{ maxWidth: 860 }}>
        <ButtonLink
          from="/gym/$venueUuid/settings/labels/archive"
          to=".."
          size="sm"
          variant="plain"
          startDecorator={<ArrowBack />}
          sx={{ ml: -1 }}
        >
          Route Labels
        </ButtonLink>
        <Typography level="title-lg" sx={{ mt: 0.5 }}>
          Archived labels
        </Typography>

        {isPending ? <CircularProgress /> : null}
        {isError ? <Alert color="danger">Couldn’t load the route labels.</Alert> : null}
        {config && archivedCount === 0 ? <Typography>Nothing has been archived.</Typography> : null}

        {hasArchivedLabels ? (
          <>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Archived labels
            </Typography>
            <Card variant="outlined" sx={{ p: 0, gap: 0, mb: 4 }}>
              {archivedGroups.map((group) => {
                const expanded = expandedUuids.has(group.uuid);
                return (
                  <Box
                    key={group.uuid}
                    sx={{
                      "&:not(:first-of-type)": { borderTop: "1px solid", borderColor: "divider" },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                      onClick={() => toggle(group.uuid)}
                      sx={{
                        minHeight: 52,
                        px: 1.5,
                        cursor: "pointer",
                        backgroundColor: expanded ? "background.level1" : undefined,
                        "&:hover": { backgroundColor: "background.level1" },
                      }}
                    >
                      <ChevronRight
                        fontSize="small"
                        sx={{ transform: expanded ? "rotate(90deg)" : undefined }}
                      />
                      <Typography level="title-md" noWrap sx={{ flex: "0 0 150px" }}>
                        {group.name}
                      </Typography>
                      <Typography
                        level="body-sm"
                        noWrap
                        sx={{ flex: 1, minWidth: 0, visibility: expanded ? "hidden" : undefined }}
                      >
                        {group.labels.map((label) => label.name).join(", ")}
                      </Typography>
                      <Button
                        size="sm"
                        variant="outlined"
                        onClick={(event) => {
                          event.stopPropagation();
                          updateGroup.mutate({ uuid: group.uuid, isActive: true });
                        }}
                      >
                        Restore label
                      </Button>
                    </Stack>
                    {expanded ? (
                      <Box
                        sx={{
                          pl: 5.5,
                          pr: 2,
                          py: 2,
                          borderTop: "1px dashed",
                          borderColor: "divider",
                        }}
                      >
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                          {group.labels.map((label) => (
                            <Chip key={label.uuid} size="lg" variant="soft" color="neutral">
                              {label.name}
                            </Chip>
                          ))}
                        </Stack>
                        <Typography level="body-sm" sx={{ mt: 1.5 }}>
                          These options can’t be restored on their own. Restore the label to use
                          them again.
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
              {archivedRootLabels.map((label) => (
                <Stack
                  key={label.uuid}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{
                    minHeight: 52,
                    px: 1.5,
                    "&:not(:first-of-type)": { borderTop: "1px solid", borderColor: "divider" },
                  }}
                >
                  {/* Lines the name up with the rows that have a chevron. */}
                  <Box aria-hidden sx={{ width: 20, flex: "none" }} />
                  <Typography level="title-md" noWrap sx={{ flex: 1 }}>
                    {label.name}
                  </Typography>
                  <Button
                    size="sm"
                    variant="outlined"
                    onClick={() => updateLabel.mutate({ uuid: label.uuid, isActive: true })}
                  >
                    Restore label
                  </Button>
                </Stack>
              ))}
            </Card>
          </>
        ) : null}

        {archivedOptionSections.length > 0 ? (
          <>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Archived options
            </Typography>
            <Card variant="outlined" sx={{ p: 0, gap: 0 }}>
              {archivedOptionSections.map((section) => (
                <Stack
                  key={section.groupName}
                  direction="row"
                  alignItems="center"
                  gap={2}
                  sx={{
                    px: 2,
                    py: 1.5,
                    "&:not(:first-of-type)": { borderTop: "1px solid", borderColor: "divider" },
                  }}
                >
                  <Typography level="title-sm" sx={{ flex: "0 0 150px" }}>
                    {section.groupName}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {section.options.map((option) => (
                      <Chip
                        key={option.uuid}
                        size="lg"
                        variant="soft"
                        color="neutral"
                        endDecorator={<Restore fontSize="small" />}
                        slotProps={{ action: { "aria-label": `Restore ${option.name}` } }}
                        onClick={() => updateLabel.mutate({ uuid: option.uuid, isActive: true })}
                      >
                        {option.name}
                      </Chip>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Card>
            <Typography level="body-sm" sx={{ mt: 1.5, maxWidth: 640 }}>
              Click an option to restore it. It returns to its original label, at the end of the
              list.
            </Typography>
          </>
        ) : null}
      </Box>
      <RouteLabelsToast />
    </Box>
  );
}
